import { EmbedService } from "@/client"
import { useBranding } from "@/hooks/useBranding"
import { isTokenExpired } from "@/utils/embedTokenUtils"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"

// Helper function to decode URL parameters (handles both + and %20 for spaces)
function decodeURLParam(param: string): string {
  return decodeURIComponent(String(param).replace(/\+/g, " "))
}

export const Route = createFileRoute("/embed/app")({
  component: EmbedApp,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      // Authentication flow (HMAC signature-based)
      program_name: (search.program_name as string) || "",
      app_id: (search.app_id as string) || "",
      signature: (search.signature as string) || "",
      student_email: (search.student_email as string) || "",
      student_name: (search.student_name as string) || "",
      student_identifier: (search.student_identifier as string) || "",
      student_alias: (search.student_alias as string) || "",
      school: (search.school as string) || "",

      // Token flow (direct access with JWT)
      token: (search.token as string) || "",

      // Error handling
      error: (search.error as string) || "",
      message: (search.message as string) || "",
      redirect_to: (search.redirect_to as string) || "",
    }
  },
})

function EmbedApp() {
  const searchParams = Route.useSearch()
  const navigate = useNavigate()
  const [isTokenStored, setIsTokenStored] = useState(false)
  const { display_name, logo_url } = useBranding()

  // Determine which flow we're in
  const hasAuthParams = Boolean(
    searchParams.program_name &&
      searchParams.app_id &&
      searchParams.signature &&
      searchParams.student_email &&
      searchParams.student_identifier,
  )

  // Clear any existing token when signature params are present
  // This ensures we always get a fresh token and don't use expired ones
  useEffect(() => {
    if (hasAuthParams) {
      const existingToken = localStorage.getItem("access_token")
      if (existingToken) {
        localStorage.removeItem("access_token")
        setIsTokenStored(false)
      }
    }
  }, [hasAuthParams])

  // Set flag to prevent AppInitializer from calling /me during auth flow
  useEffect(() => {
    if (hasAuthParams || searchParams.token) {
      sessionStorage.setItem("embed_auth_in_progress", "true")
    }
  }, [hasAuthParams, searchParams.token])

  // Authentication flow (HMAC signature validation only)
  const {
    data: authData,
    isLoading: isAuthenticating,
    error: authError,
  } = useQuery({
    queryKey: [
      "embed-app-auth",
      searchParams.program_name,
      searchParams.app_id,
      searchParams.signature,
      searchParams.student_email,
      searchParams.student_identifier,
      searchParams.student_alias,
      searchParams.school,
    ],
    queryFn: () => {
      // Decode URL parameters before sending to POST endpoint
      // Note: Replace + with spaces before decoding (+ is used for spaces in URL encoding)
      const requestBody = {
        app_id: decodeURLParam(searchParams.app_id),
        program_name: decodeURLParam(searchParams.program_name),
        student_email: decodeURLParam(searchParams.student_email),
        student_identifier: decodeURLParam(searchParams.student_identifier),
        signature: searchParams.signature, // Signature doesn't need decoding
        student_name: searchParams.student_name
          ? decodeURLParam(searchParams.student_name)
          : undefined,
        student_alias: searchParams.student_alias
          ? decodeURLParam(searchParams.student_alias)
          : undefined,
        school: searchParams.school
          ? decodeURLParam(searchParams.school)
          : undefined,
      }

      return EmbedService.authenticateEmbedAppUser({ requestBody })
    },
    enabled: hasAuthParams && !searchParams.token,
    retry: false,
  })

  // Use token from either direct param or auth response
  const activeToken = searchParams.token || authData?.access_token || ""

  // Check if token from URL param is expired
  const tokenExpired = activeToken && isTokenExpired(activeToken)

  // If token is expired, clear it and show error
  useEffect(() => {
    if (activeToken && tokenExpired) {
      localStorage.removeItem("access_token")
      setIsTokenStored(false)
    }
  }, [activeToken, tokenExpired])

  // Store token in localStorage when available (must match regular login flow)
  useEffect(() => {
    if (activeToken && !isTokenStored && !tokenExpired) {
      localStorage.setItem("access_token", activeToken)
      setIsTokenStored(true)
    }
  }, [activeToken, isTokenStored, tokenExpired])

  // Redirect to token flow after successful authentication
  useEffect(() => {
    if (authData?.access_token && hasAuthParams) {
      // Replace URL with token-based URL to continue the flow
      navigate({
        to: "/embed/app",
        search: {
          token: authData.access_token,
          redirect_to: searchParams.redirect_to,
        },
        replace: true,
      })
    }
  }, [authData, hasAuthParams, navigate, searchParams.redirect_to])

  // Once token is available, redirect to /quizzes (student default)
  useEffect(() => {
    // Only navigate to /quizzes if:
    // 1. Token is stored
    // 2. We're in the token flow (not signature flow with hasAuthParams)
    // 3. Token is not expired
    if (activeToken && isTokenStored && !hasAuthParams && !tokenExpired) {
      // Clear embed auth flag before navigation
      sessionStorage.removeItem("embed_auth_in_progress")
      // Navigate to quizzes route with token in localStorage
      navigate({
        to: searchParams.redirect_to || "/quizzes",
        replace: true,
      })
    }
  }, [
    activeToken,
    isTokenStored,
    hasAuthParams,
    tokenExpired,
    navigate,
    searchParams.redirect_to,
  ])

  // Handle error query params (from handleApiError redirect)
  if (searchParams.error === "session_expired") {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
        }}
      >
        <div
          style={{ textAlign: "center", maxWidth: "500px", padding: "2rem" }}
        >
          <img
            src={logo_url}
            alt={display_name}
            style={{
              width: 60,
              height: 60,
              marginBottom: 15,
              objectFit: "contain",
            }}
          />
          <h1 style={{ fontSize: "4rem", margin: "0", color: "#f59e0b" }}>⏱️</h1>
          <h2
            style={{ fontSize: "1.5rem", margin: "1rem 0", color: "#374151" }}
          >
            Session Expired
          </h2>
          <p style={{ color: "#6b7280", lineHeight: "1.5" }}>
            {searchParams.message ||
              "Your session has expired. Please access this page again from the original link to get a fresh authentication token."}
          </p>
          <p
            style={{
              color: "#9ca3af",
              fontSize: "0.875rem",
              marginTop: "1rem",
            }}
          >
            If you're viewing this in an iframe, the embedding page may need to
            reload.
          </p>
        </div>
      </div>
    )
  }

  // Loading states
  if (isAuthenticating) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
        }}
      >
        <img
          src={logo_url}
          alt={display_name}
          style={{
            width: 80,
            height: 80,
            marginBottom: 20,
            objectFit: "contain",
          }}
        />
        <h2
          style={{ fontSize: "1.5rem", margin: "0 0 10px 0", color: "#374151" }}
        >
          {display_name}
        </h2>
        <p style={{ color: "#6b7280" }}>Authenticating...</p>
      </div>
    )
  }

  // Authentication error
  if (authError) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
        }}
      >
        <div
          style={{ textAlign: "center", maxWidth: "500px", padding: "2rem" }}
        >
          <img
            src={logo_url}
            alt={display_name}
            style={{
              width: 60,
              height: 60,
              marginBottom: 15,
              objectFit: "contain",
            }}
          />
          <h1 style={{ fontSize: "4rem", margin: "0", color: "#dc2626" }}>
            403
          </h1>
          <h2
            style={{ fontSize: "1.5rem", margin: "1rem 0", color: "#374151" }}
          >
            Forbidden
          </h2>
          <p style={{ color: "#6b7280", lineHeight: "1.5" }}>
            {authError instanceof Error
              ? authError.message
              : "Access denied. Invalid authentication credentials."}
          </p>
        </div>
      </div>
    )
  }

  // Expired token error (when token param is provided but expired)
  if (tokenExpired && searchParams.token && !hasAuthParams) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
        }}
      >
        <div
          style={{ textAlign: "center", maxWidth: "500px", padding: "2rem" }}
        >
          <img
            src={logo_url}
            alt={display_name}
            style={{
              width: 60,
              height: 60,
              marginBottom: 15,
              objectFit: "contain",
            }}
          />
          <h1 style={{ fontSize: "4rem", margin: "0", color: "#f59e0b" }}>
            401
          </h1>
          <h2
            style={{ fontSize: "1.5rem", margin: "1rem 0", color: "#374151" }}
          >
            Session Expired
          </h2>
          <p style={{ color: "#6b7280", lineHeight: "1.5" }}>
            Your session has expired. Please access this page again from the
            original link to get a fresh authentication token.
          </p>
        </div>
      </div>
    )
  }

  return null // Will redirect to /quizzes
}
